/**
 * Vehicle Schema
 * Zod validation schemas for vehicle forms
 */

import { z } from 'zod';

/**
 * Vehicle types
 */
export const vehicleTypes = ['rolloff_truck', 'box_truck', 'pickup'] as const;

/**
 * Maintenance types
 */
export const maintenanceTypes = ['scheduled', 'repair', 'inspection'] as const;

/**
 * Create vehicle schema
 */
export const createVehicleSchema = z.object({
  vehicleNumber: z
    .string()
    .min(1, 'Vehicle number is required')
    .max(50, 'Vehicle number must be less than 50 characters')
    .regex(/^[A-Z0-9-]+$/, 'Vehicle number must contain only uppercase letters, numbers, and hyphens'),
  vehicleType: z.enum(vehicleTypes, {
    errorMap: () => ({ message: 'Please select a vehicle type' }),
  }),
  make: z
    .string()
    .min(1, 'Make is required')
    .max(50, 'Make must be less than 50 characters'),
  model: z
    .string()
    .min(1, 'Model is required')
    .max(50, 'Model must be less than 50 characters'),
  year: z
    .number({
      required_error: 'Year is required',
      invalid_type_error: 'Year must be a number',
    })
    .int('Year must be a whole number')
    .min(1900, 'Year must be 1900 or later')
    .max(new Date().getFullYear() + 1, `Year cannot be more than ${new Date().getFullYear() + 1}`),
  vin: z
    .string()
    .min(17, 'VIN must be exactly 17 characters')
    .max(17, 'VIN must be exactly 17 characters')
    .regex(/^[A-HJ-NPR-Z0-9]{17}$/, 'VIN must be 17 alphanumeric characters (excluding I, O, Q)'),
  licensePlate: z
    .string()
    .min(1, 'License plate is required')
    .max(20, 'License plate must be less than 20 characters')
    .regex(/^[A-Z0-9\s-]+$/i, 'License plate must contain only letters, numbers, spaces, and hyphens'),
  odometer: z
    .number({
      required_error: 'Odometer reading is required',
      invalid_type_error: 'Odometer must be a number',
    })
    .int('Odometer must be a whole number')
    .min(0, 'Odometer cannot be negative')
    .max(999999, 'Odometer reading is too high'),
  dotInspectionDate: z
    .string()
    .min(1, 'DOT inspection date is required')
    .refine((val) => !isNaN(Date.parse(val)), 'Invalid date format'),
  dotExpirationDate: z
    .string()
    .min(1, 'DOT expiration date is required')
    .refine((val) => !isNaN(Date.parse(val)), 'Invalid date format'),
  notes: z.string().max(500, 'Notes must be less than 500 characters').optional(),
}).refine(
  (data) => {
    const inspectionDate = new Date(data.dotInspectionDate);
    const expirationDate = new Date(data.dotExpirationDate);
    return expirationDate > inspectionDate;
  },
  {
    message: 'DOT expiration date must be after inspection date',
    path: ['dotExpirationDate'],
  }
);

/**
 * Record maintenance schema
 */
export const recordMaintenanceSchema = z.object({
  type: z.enum(maintenanceTypes, {
    errorMap: () => ({ message: 'Please select a maintenance type' }),
  }),
  description: z
    .string()
    .min(1, 'Description is required')
    .max(500, 'Description must be less than 500 characters'),
  odometer: z
    .number({
      required_error: 'Odometer reading is required',
      invalid_type_error: 'Odometer must be a number',
    })
    .int('Odometer must be a whole number')
    .min(0, 'Odometer cannot be negative')
    .max(999999, 'Odometer reading is too high'),
  cost: z
    .number({
      invalid_type_error: 'Cost must be a number',
    })
    .min(0, 'Cost cannot be negative')
    .max(99999.99, 'Cost is too high')
    .optional()
    .or(z.literal('')),
  performedBy: z
    .string()
    .max(100, 'Performed by must be less than 100 characters')
    .optional(),
});

/**
 * Update odometer schema
 */
export const updateOdometerSchema = z.object({
  newReading: z
    .number({
      required_error: 'New odometer reading is required',
      invalid_type_error: 'Odometer must be a number',
    })
    .int('Odometer must be a whole number')
    .min(0, 'Odometer cannot be negative')
    .max(999999, 'Odometer reading is too high'),
});

/**
 * Update DOT inspection schema
 */
export const updateDOTSchema = z.object({
  inspectionDate: z
    .string()
    .min(1, 'Inspection date is required')
    .refine((val) => !isNaN(Date.parse(val)), 'Invalid date format'),
  expirationDate: z
    .string()
    .min(1, 'Expiration date is required')
    .refine((val) => !isNaN(Date.parse(val)), 'Invalid date format'),
}).refine(
  (data) => {
    const inspectionDate = new Date(data.inspectionDate);
    const expirationDate = new Date(data.expirationDate);
    return expirationDate > inspectionDate;
  },
  {
    message: 'Expiration date must be after inspection date',
    path: ['expirationDate'],
  }
);

/**
 * Type exports
 */
export type CreateVehicleFormData = z.infer<typeof createVehicleSchema>;
export type RecordMaintenanceFormData = z.infer<typeof recordMaintenanceSchema>;
export type UpdateOdometerFormData = z.infer<typeof updateOdometerSchema>;
export type UpdateDOTFormData = z.infer<typeof updateDOTSchema>;
